import React, { useState, useRef, useEffect, CSSProperties } from "react";
import { IShortData } from "../../apiservice/userService.type";
import {
  SoundOutlined,
  HeartOutlined,
  MessageOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  SoundFilled,
} from "@ant-design/icons";

interface ShortCardProps {
  short: IShortData;
  isActive: boolean;
  onVideoEnded: () => void;
}

const ShortCard: React.FC<ShortCardProps> = ({
  short,
  isActive,
  onVideoEnded,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);

  const videoUrl = short?.videoUrl;

  useEffect(() => {
    if (!videoRef.current) {
      if (!videoUrl) setIsPlaying(false);
      return;
    }

    if (isActive && isPlaying) {
      videoRef.current.muted = isMuted;
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (videoRef.current && videoRef.current.muted && !isMuted) {
              // console.debug(`ShortCard ${short?.id}: Playback started but browser forced mute.`);
              setIsMuted(true);
            }
          })
          .catch((error) => {
            // console.error(`ShortCard ${short?.id}: Error attempting to play video:`, error.name, error.message);
            if (
              !isMuted &&
              (error.name === "NotAllowedError" ||
                error.name === "NotSupportedError")
            ) {
              // console.debug(`ShortCard ${short?.id}: Unmuted autoplay failed, trying muted.`);
              setIsMuted(true);
              if (videoRef.current) {
                videoRef.current.muted = true;
                videoRef.current.play().catch((mutedError) => {
                  // console.error(`ShortCard ${short?.id}: Error attempting to play video (muted):`, mutedError);
                  setIsPlaying(false);
                });
              }
            } else {
              setIsPlaying(false);
            }
          });
      }
    } else {
      videoRef.current.pause();
    }
  }, [isActive, isPlaying, isMuted, short?.id, videoUrl]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    if (!isActive && isPlaying) {
      setIsPlaying(false);
    }
    // Debug log kept for development clarity, can be removed for production
    // console.debug(`ShortCard ${short?.id} Status Update: isActive: ${isActive}, isPlaying: ${isPlaying}, isMuted: ${isMuted}`);
  }, [isActive, isPlaying, isMuted, short?.id]); // isPlaying & isMuted added back for comprehensive debug log

  const togglePlayPause = () => {
    setIsPlaying((prev) => !prev);
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMuted((prev) => !prev);
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    alert(`Liked short: ${short.title} (ID: ${short.id})`);
  };
  const handleComment = (e: React.MouseEvent) => {
    e.stopPropagation();
    alert(`Comment on short: ${short.title} (ID: ${short.id})`);
  };

  if (!short) {
    return (
      <div
        style={{
          width: "100vw",
          height: "90vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#000",
          color: "white",
        }}
      >
        <p>Error: Short data is missing.</p>
      </div>
    );
  }

  // --- Style Objects ---
  const mainCardStyle: CSSProperties = {
    width: "100vw",
    height: "90vh",
    top: "0px",
    justifyContent: "center",
    backgroundColor: "#000",
    overflow: "hidden",
  };

  const videoWrapperStyle: CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    maxHeight: "800px", // As per previous requirements
    maxWidth: "100%", // Ensures it doesn't exceed viewport width if aspect ratio + maxHeight would cause it
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#000", // For letterboxing
  };

  const videoElementStyle: CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  };

  const overlayStyle: CSSProperties = {
    position: "absolute",
    top: 20,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between", // Pushes content to top and bottom areas
    padding: "20px", // General padding for overlay content from edges
    pointerEvents: "none",
  };

  const overlayTopSectionStyle: CSSProperties = {
    display: "flex",
    justifyContent: "flex-end", // Positions items (mute button) to the right
    alignItems: "flex-start", // Aligns items to the top
    pointerEvents: "auto", // Enable pointer events for children of this section
  };

  const overlayBottomSectionStyle: CSSProperties = {
    display: "flex",
    justifyContent: "space-between", // Title/desc on left, icons on right (conceptually, icons are separate absolute group)
    alignItems: "flex-end",
  };

  const bottomInfoStyle: CSSProperties = {
    color: "white",
    textShadow: "1px 1px 4px rgba(0,0,0,0.8)", // Slightly stronger shadow for readability
    maxWidth: "calc(100% - 60px)", // Leave space for side icons
    pointerEvents: "auto",
  };

  const titleStyle: CSSProperties = {
    margin: "0 0 5px 0",
    fontSize: "1.3em", // Slightly adjusted
    fontWeight: "600", // Bolder
  };

  const descriptionStyle: CSSProperties = {
    margin: 0,
    fontSize: "0.9em",
    maxHeight: "3em", // Approx 2 lines with line-height
    lineHeight: "1.4em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  };

  const centerPlayIconStyle: CSSProperties = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontSize: "72px", // Increased size
    color: "rgba(255, 255, 255, 0.75)", // Slightly less transparent
    pointerEvents: "none",
    textShadow: "1px 1px 5px rgba(0,0,0,0.5)",
  };

  const muteButtonStyle: CSSProperties = {
    background: "rgba(30,30,30,0.5)", // Slightly darker, more subtle background
    border: "none",
    color: "white",
    padding: "12px", // Increased padding for better touch area
    borderRadius: "50%",
    cursor: "pointer",
    fontSize: "1.4em",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
  };

  const sideIconsContainerStyle: CSSProperties = {
    position: "absolute",
    right: "20px", // Consistent with overlay padding
    bottom: "15px", // Consistent with overlay padding
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "18px", // Slightly adjusted gap
    pointerEvents: "auto",
  };

  const sideIconButtonStyle: CSSProperties = {
    background: "none",
    border: "none",
    color: "white",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontSize: "2.2em", // Maintained size, AntD icons might appear smaller than FA with same font-size
    textShadow: "1px 1px 4px rgba(0,0,0,0.7)", // Stronger shadow
  };

  const iconCountStyle: CSSProperties = {
    fontSize: "0.5em", // Relative to parent (icon)
    fontWeight: "600", // Bolder count
    marginTop: "5px",
    textShadow: "none", // Counts might be clearer without shadow or with a very minimal one
  };

  return (
    <div style={mainCardStyle}>
      <div style={videoWrapperStyle} onClick={togglePlayPause}>
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            onEnded={onVideoEnded}
            style={videoElementStyle}
            playsInline
            controls={false}
          />
        ) : (
          <div style={{ textAlign: "center", color: "white", padding: "20px" }}>
            <p style={{ fontSize: "1.1em" }}>{short.title}</p>
            <p>Video content is not available.</p>
          </div>
        )}

        <div style={overlayStyle}>
          <div style={overlayTopSectionStyle}>
            <button
              onClick={handleToggleMute}
              style={muteButtonStyle}
              aria-label={isMuted ? "Unmute video" : "Mute video"}
            >
              {isMuted ? <SoundFilled /> : <SoundOutlined />}
            </button>
          </div>

          {isActive && !isPlaying && videoUrl && (
            <div style={centerPlayIconStyle}>
              <PlayCircleOutlined />
            </div>
          )}

          <div style={overlayBottomSectionStyle}>
            {" "}
            {/* This div is for structure, bottomInfo is main content here */}
            <div style={bottomInfoStyle}>
              <h3 style={titleStyle}>{short.title}</h3>
              {short.description && (
                <p
                  style={descriptionStyle}
                  dangerouslySetInnerHTML={{
                    __html: short?.description || "",
                  }}
                ></p>
              )}
            </div>
            {/* Side icons are now outside this flex flow, positioned absolutely */}
          </div>
        </div>

        <div style={sideIconsContainerStyle}>
          {/* <button onClick={handleLike} style={sideIconButtonStyle}>
            <HeartOutlined />
            <span style={iconCountStyle}>{short.likeCount ?? 0}</span>
          </button>
          <button onClick={handleComment} style={sideIconButtonStyle}>
            <MessageOutlined />
            <span style={iconCountStyle}>{short.commentCount ?? 0}</span>
          </button> */}
          <div style={{ ...sideIconButtonStyle, cursor: "default" }}>
            <EyeOutlined />
            <span style={iconCountStyle}>{short.viewCount ?? 0}</span>
          </div>
        </div>

        {/* Debug info commented out for production/clean UI */}
        {/*
        <div style={{ position: 'absolute', top: '5px', left: '5px', backgroundColor: 'rgba(0,0,0,0.7)', padding: '3px 5px', fontSize: '10px', zIndex: 10, color: 'white', pointerEvents:'none' }}>
            ID: {short.id} | Active: {isActive.toString()} | Playing: {isPlaying.toString()} | Muted: {isMuted.toString()}
        </div>
        */}
      </div>
    </div>
  );
};

export default ShortCard;
